import { useEffect, useRef } from 'react';

export function DashboardBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let stars: Star[] = [];
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
      initStars();
    };

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
    }

    interface Star {
      x: number;
      y: number;
      size: number;
      twinkleSpeed: number;
      twinkleOffset: number;
    }

    const initParticles = () => {
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 25000);
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          size: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.5 + 0.1,
          color: Math.random() > 0.7 ? '#22d3ee' : Math.random() > 0.5 ? '#a855f7' : '#3b82f6',
        });
      }
    };

    const initStars = () => {
      stars = [];
      const starCount = Math.floor((canvas.width * canvas.height) / 8000);
      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5,
          twinkleSpeed: Math.random() * 0.02 + 0.01,
          twinkleOffset: Math.random() * Math.PI * 2,
        });
      }
    };

    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.03)';
      ctx.lineWidth = 1;

      const gridSize = 60;
      const offsetX = (time * 10) % gridSize;
      const offsetY = (time * 5) % gridSize;

      for (let x = -gridSize + offsetX; x < canvas.width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      for (let y = -gridSize + offsetY; y < canvas.height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };

    const drawStars = () => {
      stars.forEach((star) => {
        const twinkle = Math.sin(time * star.twinkleSpeed * 60 + star.twinkleOffset);
        const opacity = 0.3 + twinkle * 0.3;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });
    };

    const drawParticles = () => {
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(')', `, ${p.opacity})`).replace('rgb', 'rgba');

        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2);
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fill();
      });
    };

    const drawConnections = () => {
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.05)';
      ctx.lineWidth = 0.5;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.15;
            ctx.strokeStyle = `rgba(34, 211, 238, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const drawOrbit = (centerX: number, centerY: number, radiusX: number, radiusY: number, rotation: number) => {
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(rotation);

      ctx.strokeStyle = 'rgba(168, 85, 247, 0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(0, 0, radiusX, radiusY, 0, 0, Math.PI * 2);
      ctx.stroke();

      const angle = time * 0.5;
      const satelliteX = Math.cos(angle) * radiusX;
      const satelliteY = Math.sin(angle) * radiusY;

      const gradient = ctx.createRadialGradient(satelliteX, satelliteY, 0, satelliteX, satelliteY, 8);
      gradient.addColorStop(0, 'rgba(168, 85, 247, 0.8)');
      gradient.addColorStop(0.5, 'rgba(168, 85, 247, 0.3)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(satelliteX, satelliteY, 8, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    const drawScanLine = () => {
      const scanY = ((time * 30) % (canvas.height + 100)) - 50;
      const gradient = ctx.createLinearGradient(0, scanY - 50, 0, scanY + 50);
      gradient.addColorStop(0, 'transparent');
      gradient.addColorStop(0.5, 'rgba(34, 211, 238, 0.03)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, scanY - 50, canvas.width, 100);
    };

    const drawHUDElements = () => {
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.1)';
      ctx.lineWidth = 1;

      ctx.beginPath();
      ctx.moveTo(20, 60);
      ctx.lineTo(60, 60);
      ctx.lineTo(80, 80);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(canvas.width - 20, 60);
      ctx.lineTo(canvas.width - 60, 60);
      ctx.lineTo(canvas.width - 80, 80);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(20, canvas.height - 60);
      ctx.lineTo(60, canvas.height - 60);
      ctx.lineTo(80, canvas.height - 80);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(canvas.width - 20, canvas.height - 60);
      ctx.lineTo(canvas.width - 60, canvas.height - 60);
      ctx.lineTo(canvas.width - 80, canvas.height - 80);
      ctx.stroke();

      for (let i = 0; i < 5; i++) {
        const barHeight = Math.sin(time * 2 + i) * 10 + 20;
        const x = 30 + i * 12;
        const y = canvas.height - 90;
        ctx.fillStyle = `rgba(34, 211, 238, ${0.2 + i * 0.1})`;
        ctx.fillRect(x, y, 8, -barHeight);
      }
    };

    const animate = () => {
      time += 0.016;

      ctx.fillStyle = 'rgba(10, 10, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width
      );
      gradient.addColorStop(0, 'rgba(20, 10, 40, 0.5)');
      gradient.addColorStop(0.5, 'rgba(10, 10, 30, 0.3)');
      gradient.addColorStop(1, 'rgba(5, 5, 15, 0.1)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawGrid();
      drawStars();
      drawConnections();
      drawParticles();
      drawScanLine();
      drawOrbit(canvas.width * 0.8, canvas.height * 0.3, 150, 80, Math.PI / 6);
      drawOrbit(canvas.width * 0.2, canvas.height * 0.7, 100, 50, -Math.PI / 4);
      drawHUDElements();

      animationFrameId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full opacity-60"
      />

      <div className="absolute top-24 left-8 hidden lg:block">
        <div className="space-y-2 animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 shadow-lg shadow-green-500/50" />
            <span className="text-xs text-cyan-400/40 font-mono">SYS.ONLINE</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-cyan-500 shadow-lg shadow-cyan-500/50" />
            <span className="text-xs text-cyan-400/40 font-mono">AI.READY</span>
          </div>
        </div>
      </div>

      <div className="absolute top-24 right-8 hidden lg:block">
        <div className="text-right space-y-1">
          <div className="text-xs text-purple-400/40 font-mono animate-pulse">
            OCHTOPUS v2.0
          </div>
          <div className="text-xs text-cyan-400/30 font-mono">
            QUANTUM CORE
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-8 hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="flex gap-1">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-gradient-to-t from-cyan-500/20 to-cyan-500/60 rounded-full animate-pulse"
                style={{
                  height: `${10 + Math.random() * 20}px`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
          <span className="text-xs text-cyan-400/30 font-mono">NEURAL.SYNC</span>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 hidden lg:block">
        <div className="w-16 h-16 relative">
          <div className="absolute inset-0 border border-purple-500/20 rounded-full animate-spin" style={{ animationDuration: '10s' }}>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-purple-500/50 rounded-full" />
          </div>
          <div className="absolute inset-2 border border-cyan-500/20 rounded-full animate-spin" style={{ animationDuration: '7s', animationDirection: 'reverse' }}>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-cyan-500/50 rounded-full" />
          </div>
          <div className="absolute inset-4 border border-blue-500/20 rounded-full animate-spin" style={{ animationDuration: '5s' }}>
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-500/50 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
